/* ──────────────────────────────────────────────────────────────────────────
   Rawtorque: real-user INP attribution beacon, v2 (script attribution)
   v1 built 5 Sep 2026, v2 built 11 Sep 2026.

   WHY v2
   The first day of v1 data (53 real interactions) showed that 71% of slow
   interaction time is INPUT DELAY: the visitor taps while the main thread is
   already busy running something else, and the tap waits. Event handlers
   themselves take ~0 ms. So the element tapped is not the cause; whatever was
   running at the time is. v1 could not see that.

   v2 adds Long Animation Frame (LoAF) attribution. For the frames that
   overlapped the worst interaction, it records which scripts were running
   (file, function, what invoked them) and for how long. That names the script
   that kept the phone busy. LoAF is Chromium-only (Chrome/Edge/Android), which
   is most of this store's mobile traffic; on Safari/Firefox those fields are
   simply empty and everything else still reports.

   It also records `at`, when in the page view the interaction happened (ms
   since navigation), because v1 showed late taps are far slower than early ones.

   COST
   Still no library, no DOM access, no layout reads. Two native
   PerformanceObservers and a small capped buffer; all work happens in the
   `visibilitychange` handler when the page is already going away.
   ────────────────────────────────────────────────────────────────────────── */
(function () {
	'use strict';

	var ENDPOINT   = 'https://rawtorque-n8n.onrender.com/webhook/rt-inp';
	var SAMPLE     = 0.25;   /* share of sessions that report */
	var MIN_REPORT = 200;    /* only report interactions at/over this (ms), the Good threshold */
	var LOAF_CAP   = 300;    /* max long frames kept in memory per page view */

	var PO = window.PerformanceObserver;
	var types = (PO && PO.supportedEntryTypes) || [];
	if (!PO || types.indexOf('event') === -1) return;

	if (Math.random() > SAMPLE) return;

	var worst = null;
	var count = 0;
	var lcp = null;
	var cls = 0;
	var sent = false;
	var loafs = [];

	/* short, stable selector: tag + id + at most two classes, never text */
	function selectorFor(el) {
		if (!el || el.nodeType !== 1) return '(none)';
		var parts = [];
		var node = el;
		for (var depth = 0; node && node.nodeType === 1 && depth < 3; depth++) {
			var s = node.tagName.toLowerCase();
			if (node.id) { s += '#' + String(node.id).slice(0, 30); parts.unshift(s); break; }
			var cn = node.getAttribute && node.getAttribute('class');
			if (cn) {
				var c2 = String(cn).trim().split(/\s+/).slice(0, 2).join('.');
				if (c2) s += '.' + c2.slice(0, 40);
			}
			parts.unshift(s);
			node = node.parentElement;
		}
		return parts.join('>').slice(0, 120);
	}

	function templateName() {
		var b = document.body;
		if (!b) return 'unknown';
		var m = String(b.className).match(/template-([a-z0-9_-]+)/i);
		return m ? m[1].slice(0, 24) : 'unknown';
	}

	/* script URL -> short label. Query strings are always dropped. */
	var pageUrl = location.origin + location.pathname;
	function shortUrl(u) {
		if (!u) return '(unknown)';
		var clean = String(u).split('#')[0].split('?')[0];
		if (clean === pageUrl) return '(inline)';
		try {
			var a = new URL(clean);
			var file = a.pathname.split('/').pop() || a.pathname;
			return (a.host === location.host ? '' : a.host + '/') + file.slice(0, 60);
		} catch (e) {
			return clean.slice(-60);
		}
	}

	function trimInvoker(s) {
		if (!s) return '';
		/* invokers can be URLs too; shorten those the same way */
		s = String(s);
		if (/^https?:\/\//.test(s)) return shortUrl(s);
		return s.slice(0, 60);
	}

	try {
		new PO(function (list) {
			var es = list.getEntries();
			for (var i = 0; i < es.length; i++) {
				var e = es[i];
				if (!e.interactionId) continue;          /* only real interactions count toward INP */
				count++;
				if (!worst || e.duration > worst.duration) worst = e;
			}
		}).observe({ type: 'event', buffered: true, durationThreshold: 16 });
	} catch (err) { return; }

	if (types.indexOf('long-animation-frame') !== -1) {
		try {
			new PO(function (list) {
				var es = list.getEntries();
				for (var i = 0; i < es.length; i++) {
					var f = es[i];
					var sc = [];
					var fs = f.scripts || [];
					for (var j = 0; j < fs.length; j++) {
						var x = fs[j];
						sc.push({
							u: shortUrl(x.sourceURL),
							fn: String(x.sourceFunctionName || '').slice(0, 50),
							inv: trimInvoker(x.invoker),
							t: x.invokerType || '',
							d: Math.round(x.duration || 0),
							fsl: Math.round(x.forcedStyleAndLayoutDuration || 0)
						});
					}
					loafs.push({
						s: f.startTime,
						d: f.duration,
						b: f.blockingDuration || 0,
						sl: f.styleAndLayoutStart ? Math.max(0, f.startTime + f.duration - f.styleAndLayoutStart) : 0,
						sc: sc
					});
					if (loafs.length > LOAF_CAP) loafs.shift();
				}
			}).observe({ type: 'long-animation-frame', buffered: true });
		} catch (err) { }
	}

	try {
		new PO(function (l) {
			var es = l.getEntries();
			if (es.length) lcp = Math.round(es[es.length - 1].startTime);
		}).observe({ type: 'largest-contentful-paint', buffered: true });
	} catch (err) { }

	try {
		new PO(function (l) {
			var es = l.getEntries();
			for (var i = 0; i < es.length; i++) if (!es[i].hadRecentInput) cls += es[i].value;
		}).observe({ type: 'layout-shift', buffered: true });
	} catch (err) { }

	/* which scripts were running during the worst interaction */
	function attribute(w) {
		var ws = w.startTime;
		var we = w.startTime + w.duration;
		var wd = w.processingStart;               /* end of the input-delay window */
		var n = 0, ms = 0, blk = 0, sty = 0;
		var agg = {};
		for (var i = 0; i < loafs.length; i++) {
			var f = loafs[i];
			if (!(f.s < we && f.s + f.d > ws)) continue;
			n++; ms += f.d; blk += f.b; sty += f.sl;
			var inDelay = f.s < wd && f.s + f.d > ws;
			for (var j = 0; j < f.sc.length; j++) {
				var x = f.sc[j];
				var key = x.u + ' :: ' + (x.fn || '-') + ' :: ' + (x.inv || '-');
				var a = agg[key] || (agg[key] = { u: x.u, fn: x.fn, inv: x.inv, t: x.t, ms: 0, fsl: 0, delay: false });
				a.ms += x.d; a.fsl += x.fsl;
				if (inDelay) a.delay = true;
			}
		}
		var list = [];
		for (var k in agg) if (Object.prototype.hasOwnProperty.call(agg, k)) list.push(agg[k]);
		list.sort(function (p, q) { return q.ms - p.ms; });
		function label(a) { return a ? (a.u + ' :: ' + (a.fn || '-') + ' :: ' + (a.inv || '-')).slice(0, 160) : ''; }
		var top = list.slice(0, 5);
		var json = '';
		try { json = JSON.stringify(top).slice(0, 1200); } catch (e) { }
		return {
			loaf_n: n,
			loaf_ms: Math.round(ms),
			loaf_block_ms: Math.round(blk),
			loaf_style_ms: Math.round(sty),
			s1: label(list[0]), s1_ms: list[0] ? list[0].ms : null,
			s2: label(list[1]), s2_ms: list[1] ? list[1].ms : null,
			s3: label(list[2]), s3_ms: list[2] ? list[2].ms : null,
			scripts: json
		};
	}

	function report() {
		if (sent || !worst) return;
		var d = Math.round(worst.duration);
		if (d < MIN_REPORT) return;
		sent = true;

		var nav = navigator.connection || {};
		var payload = {
			v: 2,
			ts: Date.now(),
			path: location.pathname.slice(0, 120),      /* pathname only, no query string */
			tpl: templateName(),
			inp: d,
			delay: Math.round(worst.processingStart - worst.startTime),
			proc: Math.round(worst.processingEnd - worst.processingStart),
			render: Math.round(worst.startTime + worst.duration - worst.processingEnd),
			at: Math.round(worst.startTime),
			evt: worst.name,
			target: selectorFor(worst.target),
			interactions: count,
			lcp: lcp,
			cls: Math.round(cls * 1000) / 1000,
			vw: window.innerWidth,
			dpr: Math.round((window.devicePixelRatio || 1) * 10) / 10,
			conn: nav.effectiveType || null,
			mem: navigator.deviceMemory || null,
			cpu: navigator.hardwareConcurrency || null
		};
		var att = attribute(worst);
		for (var k in att) if (Object.prototype.hasOwnProperty.call(att, k)) payload[k] = att[k];

		var body = JSON.stringify(payload);

		/* IMPORTANT: the content type must stay text/plain. It is a CORS-safelisted
		   value, so the request goes out with no preflight. application/json makes
		   the browser attempt an OPTIONS preflight, which sendBeacon cannot do, and
		   the beacon is dropped SILENTLY. The n8n workflow parses the raw string. */
		try {
			if (navigator.sendBeacon &&
			    navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'text/plain;charset=UTF-8' }))) return;
		} catch (err) { }
		try {
			fetch(ENDPOINT, { method: 'POST', body: body, keepalive: true, mode: 'no-cors' });
		} catch (err) { }
	}

	document.addEventListener('visibilitychange', function () {
		if (document.visibilityState === 'hidden') report();
	}, { capture: true });
	window.addEventListener('pagehide', report, { capture: true });
})();

/* ──────────────────────────────────────────────────────────────────────────
   PRIVACY
   Nothing identifying is collected. No cookie, no id, no localStorage, no
   fingerprint. `path` is location.pathname only. `target` is built from tag
   names, ids and class names, never element text. Script attribution records
   script FILE names (query strings dropped), function names and invoker labels
   such as "DIV.onclick" or "TimerHandler:setTimeout", which describe code, not
   people. Inline scripts are labelled "(inline)" rather than by page URL. One
   beacon per page view, from 25% of sessions, only when an interaction was
   slower than 200 ms.
   ────────────────────────────────────────────────────────────────────────── */
